import React, { useState, useEffect, useCallback } from 'react';
import { accountsAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  CheckCircle2, AlertTriangle, XCircle, Clock, Radio, 
  ChevronDown, Shield, Activity, Server
} from 'lucide-react';

const STATUS_CONFIG = {
  FRESH: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', dot: 'bg-emerald-400', label: 'Healthy' },
  DEGRADED: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', dot: 'bg-amber-400', label: 'Degraded' },
  STALE: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', dot: 'bg-amber-400', label: 'Stale' },
  CRITICAL: { icon: XCircle, color: 'text-rose-400', bg: 'bg-rose-500/15', border: 'border-rose-500/30', dot: 'bg-rose-400', label: 'Failed' },
  UNKNOWN: { icon: Radio, color: 'text-zinc-400', bg: 'bg-zinc-500/15', border: 'border-zinc-500/30', dot: 'bg-zinc-400', label: 'Unknown' },
};

const SEVERITY_BADGE = {
  CRITICAL: 'bg-rose-500/20 text-rose-300 border-rose-500/40',
  WARNING: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
};

const ComponentHealth = ({ label, data }) => {
  if (!data) return null;
  const health = data.health || 'SKIPPED';
  const isHealthy = health === 'HEALTHY';
  const isDegraded = health === 'DEGRADED' || health === 'PARTIAL';
  
  return (
    <div className="flex items-center justify-between py-1.5" data-testid={`watchdog-component-${label}`}>
      <div className="flex items-center gap-2">
        <div className={`w-1.5 h-1.5 rounded-full ${isHealthy ? 'bg-emerald-400' : isDegraded ? 'bg-amber-400' : 'bg-rose-400'}`} />
        <span className="text-xs text-muted-foreground capitalize">{label}</span>
      </div>
      <div className="flex items-center gap-2 text-xs font-mono tabular-nums">
        {data.synced > 0 && <span className="text-emerald-400">+{data.synced}</span>}
        {data.updated > 0 && <span className="text-blue-400">{data.updated} upd</span>}
        {data.failed > 0 && <span className="text-rose-400">{data.failed} fail</span>}
        {data.synced === 0 && data.updated === 0 && data.failed === 0 && (
          <span className="text-zinc-500">{health.toLowerCase()}</span>
        )}
      </div>
    </div>
  );
};

const SyncStatusPill = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await accountsAPI.getWatchdogStatus();
      setStatus(res.data);
    } catch {
      // Silently fail — pill shows UNKNOWN
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000); // Refresh every 60s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (loading || !status) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-800/50 border border-zinc-700/50 animate-pulse" data-testid="sync-status-pill-loading">
        <div className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
        <span className="text-xs text-zinc-500">Sync</span>
      </div>
    );
  }

  const freshness = status.freshness || { status: 'UNKNOWN', label: 'No data', color: 'gray' };
  const config = STATUS_CONFIG[freshness.status] || STATUS_CONFIG.UNKNOWN;
  const StatusIcon = config.icon;
  const alerts = status.active_alerts || [];
  const hasCritical = status.critical_alert_count > 0;
  const latest = status.latest_sync;
  const components = latest?.component_health;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 gap-1.5 px-2.5 rounded-full ${config.bg} ${config.border} border hover:opacity-90 transition-opacity`}
          data-testid="sync-status-pill"
        >
          <span className={`relative flex h-2 w-2`}>
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.dot} opacity-50`}
              style={{ animationDuration: freshness.status === 'FRESH' ? '3s' : '1.5s' }} />
            <span className={`relative inline-flex rounded-full h-2 w-2 ${config.dot}`} />
          </span>
          <span className={`text-xs font-medium ${config.color}`}>
            {freshness.label}
          </span>
          {alerts.length > 0 && (
            <Badge variant="outline" className={`text-[10px] px-1 py-0 h-4 ml-0.5 ${hasCritical ? 'border-rose-500/50 text-rose-400' : 'border-amber-500/50 text-amber-400'}`}>
              {alerts.length}
            </Badge>
          )}
          <ChevronDown className={`w-3 h-3 ${config.color} transition-transform ${open ? 'rotate-180' : ''}`} />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-80 p-0 bg-zinc-900 border-zinc-800 shadow-xl"
        align="end"
        data-testid="sync-status-popover"
      >
        {/* Header */}
        <div className={`px-4 py-3 border-b border-zinc-800 ${config.bg}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${config.color}`} />
              <span className={`text-sm font-semibold ${config.color}`}>
                Sync {config.label}
              </span>
            </div>
            {status.enabled && (
              <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">
                Every {status.interval_minutes}m
              </Badge>
            )}
          </div>
          {latest?.auth_method && (
            <div className="flex items-center gap-1 mt-1.5">
              <Shield className="w-3 h-3 text-zinc-500" />
              <span className="text-[10px] text-zinc-500">
                Auth: {latest.auth_method === 'SERVICE_ACCOUNT' ? 'Service Account' :
                       latest.auth_method === 'CACHED_TOKEN' ? 'Cached Token (degraded)' :
                       latest.auth_method === 'FAILED' ? 'Failed' : latest.auth_method}
              </span>
            </div>
          )}
        </div>

        {/* Component Health */}
        {components && (
          <div className="px-4 py-2.5 border-b border-zinc-800">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Server className="w-3 h-3 text-zinc-500" />
              <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Component Health</span>
            </div>
            <ComponentHealth label="vendors" data={components.vendors} />
            <ComponentHealth label="bills" data={components.bills} />
            {components.bills?.stale_guard_skipped && (
              <div className="mt-1 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/20">
                <span className="text-[10px] text-amber-400">Stale guard blocked — {components.bills.stale_guard_reason}</span>
              </div>
            )}
          </div>
        )}

        {/* Active Alerts */}
        {alerts.length > 0 && (
          <div className="px-4 py-2.5 border-b border-zinc-800">
            <div className="flex items-center gap-1.5 mb-1.5">
              <AlertTriangle className="w-3 h-3 text-zinc-500" />
              <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Active Alerts</span>
            </div>
            <div className="space-y-1.5">
              {alerts.map((alert) => (
                <div key={alert.alert_id} className="flex items-start gap-2" data-testid={`watchdog-alert-${alert.alert_type}`}>
                  <Badge variant="outline" className={`text-[9px] px-1 py-0 h-4 mt-0.5 shrink-0 ${SEVERITY_BADGE[alert.severity] || SEVERITY_BADGE.WARNING}`}>
                    {alert.severity}
                  </Badge>
                  <span className="text-[11px] text-zinc-300 leading-tight">{alert.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer: Next Run + Timestamps */}
        <div className="px-4 py-2.5 space-y-1">
          {latest?.timestamp && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-500">Last sync</span>
              <span className="text-[10px] text-zinc-400 font-mono">
                {new Date(latest.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
          {latest?.duration_ms != null && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-500">Duration</span>
              <span className="text-[10px] text-zinc-400 font-mono">
                {latest.duration_ms > 1000 ? `${(latest.duration_ms / 1000).toFixed(1)}s` : `${latest.duration_ms}ms`}
              </span>
            </div>
          )}
          {status.next_scheduled_run && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-500">Next run</span>
              <span className="text-[10px] text-zinc-400 font-mono">
                {new Date(status.next_scheduled_run).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-zinc-500">Watchdog</span>
            <div className="flex items-center gap-1">
              <Activity className={`w-2.5 h-2.5 ${status.enabled ? 'text-emerald-400' : 'text-zinc-500'}`} />
              <span className={`text-[10px] ${status.enabled ? 'text-emerald-400' : 'text-zinc-500'}`}>
                {status.enabled ? 'Active' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SyncStatusPill;
